import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Card from '../common/Card';
import Table from '../common/Table';
import Modal from '../common/Modal';
import './Disputes.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Disputes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const getToken = () => localStorage.getItem('marilink_token');

  const fetchDisputes = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/disputes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDisputes(data.disputes);
      }
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  const handleResolve = async () => {
    if (!resolution) {
      alert('Please select a resolution type');
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/disputes/${selectedDispute._id}/resolve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ resolution, notes: resolutionNotes })
      });
      const data = await response.json();
      if (data.success) {
        alert('Dispute resolved successfully');
        setShowResolveModal(false);
        fetchDisputes();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error resolving dispute: ' + error.message);
    }
  };

  const filteredDisputes = disputes.filter(dispute => {
    const matchesSearch = dispute._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          dispute.vesselName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || dispute.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: disputes.length,
    open: disputes.filter(d => d.status === 'open').length,
    investigating: disputes.filter(d => d.status === 'investigating').length,
    resolved: disputes.filter(d => d.status === 'resolved').length
  };

  const columns = [
    { key: '_id', label: 'Dispute ID', render: (val) => val?.slice(-8) },
    { key: 'vesselName', label: 'Vessel' },
    { key: 'raisedBy', label: 'Raised By' },
    { key: 'against', label: 'Against' },
    { key: 'amount', label: 'Amount', render: (val) => `$${val?.toLocaleString()}` },
    { key: 'status', label: 'Status', render: (val) => (
      <span className={`status-badge status-${val}`}>{val}</span>
    )},
    { key: 'createdAt', label: 'Date', render: (val) => new Date(val).toLocaleDateString() }
  ];

  const actions = [
    { icon: 'fa-eye', label: 'View Details', onClick: (row) => navigate(`/admin/dispute/${row._id}`) },
    { icon: 'fa-gavel', label: 'Resolve', onClick: (row) => { setSelectedDispute(row); setShowResolveModal(true); } }
  ];

  if (loading) return <div className="loading-spinner">Loading disputes...</div>;

  return (
    <div className="admin-disputes-page">
      <div className="page-header">
        <h1><i className="fas fa-gavel"></i> Disputes Management</h1>
        <p>Review and resolve disputes between owners and service providers</p>
      </div>

      <div className="stats-grid">
        <Card title="Total Disputes" value={stats.total} icon="fa-gavel" />
        <Card title="Open" value={stats.open} icon="fa-exclamation-circle" color="warning" />
        <Card title="Investigating" value={stats.investigating} icon="fa-search" color="info" />
        <Card title="Resolved" value={stats.resolved} icon="fa-check-circle" color="success" />
      </div>

      <div className="filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>
            All ({stats.total})
          </button>
          <button className={`filter-tab ${filterStatus === 'open' ? 'active' : ''}`} onClick={() => setFilterStatus('open')}>
            Open ({stats.open})
          </button>
          <button className={`filter-tab ${filterStatus === 'investigating' ? 'active' : ''}`} onClick={() => setFilterStatus('investigating')}>
            Investigating ({stats.investigating})
          </button>
          <button className={`filter-tab ${filterStatus === 'resolved' ? 'active' : ''}`} onClick={() => setFilterStatus('resolved')}>
            Resolved ({stats.resolved})
          </button>
        </div>
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Search disputes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <Table columns={columns} data={filteredDisputes} actions={actions} emptyMessage="No disputes found" />

      <Modal isOpen={showResolveModal} onClose={() => setShowResolveModal(false)} title="Resolve Dispute">
        {selectedDispute && (
          <div className="resolve-modal">
            <div className="dispute-summary">
              <p><strong>Dispute ID:</strong> {selectedDispute._id?.slice(-8)}</p>
              <p><strong>Amount:</strong> ${selectedDispute.amount?.toLocaleString()}</p>
              <p><strong>Reason:</strong> {selectedDispute.reason}</p>
            </div>
            <div className="form-group">
              <label>Resolution Type *</label>
              <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                <option value="">Select resolution</option>
                <option value="release">Release Payment to Vendor</option>
                <option value="refund">Refund to Owner</option>
                <option value="partial">Partial Refund (50/50)</option>
                <option value="dismiss">Dismiss Dispute</option>
              </select>
            </div>
            <div className="form-group">
              <label>Resolution Notes</label>
              <textarea rows="3" value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} placeholder="Add notes about this resolution..." />
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowResolveModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleResolve}>Resolve Dispute</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Disputes;